import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useGlobalContext } from '@/context/GlobalContext';
import { filterTopItems } from '@/utils/formatItems';
import { useOnClickOutside } from 'usehooks-ts';

import { useCourseData, useTopCollegeData, useTopCourseData, useTopExamData } from '@/lib/api/apiData';
import { useEntityData } from '@/hooks/useEntityData';
import useFetchEntities from '@/hooks/useEntityDataWithoutCaching';

const useUniversalSearchbarHook = () => {
  const { searchBoxOpen, setSearchBoxOpen } = useGlobalContext();

  const { courseData } = useCourseData();
  const { topCollegeData } = useTopCollegeData();
  const { topExamData } = useTopExamData();
  const { topCourseData } = useTopCourseData();
  const [inputValue, setInputValue] = useState('');
  const containerRef = useRef<any>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const router = useRouter();

  const collegeQueryParams = useMemo(
    () => ({
      sort: 'display_sequence:asc',
      fields: ['college_name', 'college_url', 'abbreviation', 'is_top', 'is_online_college'],
      filters: {
        $or: [
          { college_name: { $startsWithi: inputValue.toLowerCase() } },
          { abbreviation: { $startsWithi: inputValue.toLowerCase() } },
        ],
      },
      populate: {
        logo: { fields: ['id', 'url', 'name'] },
        country: {
          fields: ['id', 'country_name', 'country_url'],
        },
        college_navbars: { fields: ['id', 'title'] },
      },
      pagination: {
        page: 1,
        pageSize: 25,
      },
    }),
    [inputValue],
  );

  const { data: searchCollegeData, isLoading: isSearchCollegeLoading } = useFetchEntities(
    'colleges',
    collegeQueryParams,
    inputValue,
  );

  const examQueryParams = useMemo(
    () => ({
      sort: 'display_sequence:asc',
      fields: ['exam_name', 'abbreviation', 'exam_url', 'is_top'],
      filters: {
        $or: [
          { exam_name: { $startsWithi: inputValue.toLowerCase() } },
          { abbreviation: { $startsWithi: inputValue.toLowerCase() } },
        ],
      },
      populate: {
        logo: { fields: ['id', 'url', 'name'] },
        exam_navbars: { fields: ['id', 'title'], limit: 1 },
      },
      publicationState: 'live',
      pagination: {
        page: 1,
        pageSize: 25,
      },
    }),
    [inputValue],
  );

  const newsQueryParams = useMemo(
    () => ({
      sort: 'updatedAt:desc',
      // fields: ["author_name", "excerpt", "name", "url", "is_top", "updatedAt"],
      fields: ['name', 'url', 'is_top', 'updatedAt'],
      populate: {
        // { name: "featured_image", fields: ["id", "url", "name"] },
        // { name: "streams", fields: ["id", "stream_name"] },
        categories: { fields: ['category_name'] },
      },
    }),
    [inputValue],
  );
  const courseQueryParams = useMemo(
    () => ({
      sort: 'display_sequence:asc',
      fields: ['course_name', 'abbreviation', 'course_url', 'is_top'],
      filters: {
        $or: [
          { course_name: { $startsWithi: inputValue.toLowerCase() } },
          { abbreviation: { $startsWithi: inputValue.toLowerCase() } },
        ],
      },
      populate: {
        logo: { fields: ['id', 'url', 'name'] },
        course_navbars: { fields: ['id', 'title'], limit: 1 },
        page_data: {
          fields: ['id'],
        },
      },
      pagination: {
        page: 1,
        pageSize: 25,
      },
    }),
    [inputValue],
  );

  const { data: searchExamData, isLoading: isSearchExamLoading }: any = useFetchEntities(
    'exams',
    examQueryParams,
    inputValue,
  );
  const instituteQueryParams = useMemo(
    () => ({
      sort: 'display_sequence:asc',
      publicationState: 'live',
      fields: ['id', 'institute_name', 'institute_address', 'institute_url', 'institute_google_reviews'],
      populate: {
        logo: { fields: ['url'] },
      },
      filters: {
        $or: [
          { institute_name: { $startsWithi: inputValue.toLowerCase() } },
          { abbreviation: { $startsWithi: inputValue.toLowerCase() } },
        ],
      },
      pagination: {
        page: 1,
        pageSize: 25,
      },
    }),
    [inputValue],
  );
  const { data: searchInstituteData, isLoading: isSearchInstituteLoading } = useFetchEntities(
    'institute-details',
    instituteQueryParams,
    inputValue,
  );
  const { data: newsData }: any = useEntityData('news-and-blogs', newsQueryParams);
  const { data: searchCourseData, isLoading: isSearchCourseData } = useFetchEntities(
    'courses',
    courseQueryParams,
    inputValue,
  );

  const filteredCollegeData = filterTopItems(topCollegeData);
  const filteredExamData = filterTopItems(topExamData);
  const filteredNewsData = filterTopItems(newsData?.data?.results);
  const filteredCourseData = topCourseData;

  const filterCourse = searchCourseData?.data?.results?.filter((course: any) => course?.page_data?.length > 0);

  useOnClickOutside(containerRef, (event) => {
    if (inputRef?.current?.contains(event.target as Node) || dropdownRef?.current?.contains(event.target as Node)) {
      return;
    }
    setSearchBoxOpen(false);
    setInputValue('');
    document.body.style.overflow = '';
  });

  const handleInputFocus = () => {
    setSearchBoxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const handleLinkClick = () => {
    setSearchBoxOpen(false);
    setInputValue('');
    document.body.style.overflow = '';
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };
  // const processTopCourses = (courses: any[]) => {
  //   if (!courses || !Array.isArray(courses)) return [];
  //   // Return only top courses for the initial dropdown
  //   return courses.filter((course) => course?.is_top === true);
  // };
  const filterResults = () => {
    const combinedResults = [
      ...(searchCollegeData?.data?.results ?? []),
      ...(searchExamData?.data?.results ?? []),
      ...(searchCourseData?.data?.results ?? []),
      ...(searchInstituteData?.data?.results ?? []), // Add this line
    ].slice(0, 15);

    return combinedResults;
  };

  const matchedResults = inputValue?.length > 2 ? filterResults() : [];

  useEffect(() => {
    if (searchBoxOpen) {
      document.body.style.overflow = 'hidden';
      inputRef.current?.focus();
    }
  }, [searchBoxOpen]);
  //  const topCourses = useMemo(() => {
  //     return processTopCourses(courseData || []);
  //   }, [courseData]);

  // Build top stream and course suggestions when input starts with 'top'
  const topSuggestions = React.useMemo(() => {
    const input = inputValue.trim().toLowerCase();
    if (!input.startsWith('top')) return [];

    // Get the search term after 'top'
    const searchTerm = input.replace(/^top/, '').trim();

    // Use all valid courses (with data) for initial suggestions
    const validCourses = (courseData || []).filter((course: any) => course?.page_data?.length > 0);

    // Gather all unique streams and only valid courses
    const streamSet = new Set<string>();
    const courseSet = new Set<string>();
    validCourses.forEach((course: any) => {
      if (Array.isArray(course.streams)) {
        course.streams.forEach((stream: any) => {
          if (stream && typeof stream.stream_name === 'string' && stream.stream_name.trim()) {
            streamSet.add(stream.stream_name.trim());
          }
        });
      }
      if (course.stream_name && typeof course.stream_name === 'string' && course.stream_name.trim()) {
        streamSet.add(course.stream_name.trim());
      }
      if (course.course_name && typeof course.course_name === 'string' && course.course_name.trim()) {
        courseSet.add(course.course_name.trim());
      }
    });

    // If only 'top' is typed, show a limited number of suggestions
    if (!searchTerm) {
      const streamSuggestions = Array.from(streamSet)
        .sort()
        .slice(0, 5)
        .map((stream) => ({
          type: 'stream',
          label: `Top ${stream} Colleges`,
          value: stream,
        }));
      const courseSuggestions = Array.from(courseSet)
        .sort()
        .slice(0, 5)
        .map((course) => ({
          type: 'course',
          label: `Top ${course} Colleges`,
          value: course,
        }));
      return [...streamSuggestions, ...courseSuggestions];
    }

    // If user types more, filter by searchTerm
    const filterByTerm = (str: string) => str.toLowerCase().includes(searchTerm);

    const streamSuggestions = Array.from(streamSet)
      .filter(filterByTerm)
      .sort()
      .map((stream) => ({
        type: 'stream',
        label: `Top ${stream} Colleges`,
        value: stream,
      }));
    const courseSuggestions = Array.from(courseSet)
      .filter(filterByTerm)
      .sort()
      .map((course) => ({
        type: 'course',
        label: `Top ${course} Colleges`,
        value: course,
      }));

    return [...streamSuggestions, ...courseSuggestions];
  }, [inputValue, courseData]);

  // Helper to slugify names for URL
  const slugify = (str: string) =>
    str
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  // Handler for suggestion click
  const handleTopSuggestionClick = (suggestion: any) => {
    let url = '';
    const validCourses = (courseData || []).filter((course: any) => course?.page_data?.length > 0);
    if (suggestion.type === 'stream') {
      url = `/${slugify(suggestion.value)}-colleges-in-india`;
    } else if (suggestion.type === 'course') {
      // Find the course object to get its course_url
      const courseObj = validCourses.find(
        (course: any) =>
          course.course_name && course.course_name.trim().toLowerCase() === suggestion.value.trim().toLowerCase(),
      );
      if (courseObj && courseObj.course_url) {
        url = `/${courseObj.course_url}-colleges-in-india`;
      } else {
        url = `/${slugify(suggestion.value)}-colleges-in-india`;
      }
    }
    router.push(url);
    setInputValue('');
    setSearchBoxOpen(false);
  };

  // Build sets for all streams and courses for quick lookup
  const allStreams = useMemo(() => {
    const set = new Set<string>();
    (courseData || []).forEach((course: any) => {
      if (Array.isArray(course.streams)) {
        course.streams.forEach((stream: any) => {
          if (stream && typeof stream.stream_name === 'string' && stream.stream_name.trim()) {
            set.add(stream.stream_name.trim().toLowerCase());
          }
        });
      }
    });
    return set;
  }, [courseData]);

  const allCourses = useMemo(() => {
    const set = new Set<string>();
    (courseData || []).forEach((course: any) => {
      if (course.course_name && typeof course.course_name === 'string' && course.course_name.trim()) {
        set.add(course.course_name.trim().toLowerCase());
      }
    });
    return set;
  }, [courseData]);

  // Handler for search submit (Enter key or search button)
  const handleSearchSubmit = () => {
    const input = inputValue.trim().toLowerCase();
    if (allStreams.has(input)) {
      router.push(`/${slugify(inputValue.trim())}-colleges-in-india`);
      setInputValue('');
      setSearchBoxOpen(false);
      return;
    }
    if (allCourses.has(input)) {
      router.push(`/${slugify(inputValue.trim())}-colleges-in-india`);
      setInputValue('');
      setSearchBoxOpen(false);
      return;
    }
    // fallback: do nothing or show no results
  };

  // const handleResultClick = (result: any) => {
  //   if (result.institute_url) {
  //     router.push(`/institutes/${result.institute_url}`);
  //   } else if (result.college_url) {
  //     router.push(`/colleges/${result.college_url}`);
  //   } else if (result.exam_url) {
  //     router.push(`/exams/${result.exam_url}`);
  //   } else if (result.course_url) {
  //     router.push(`/courses/${result.course_url}`);
  //   }
  //   setInputValue('');
  //   setSearchBoxOpen(false);
  // };

  return {
    containerRef,
    dropdownRef,
    searchBoxOpen,
    inputRef,
    inputValue,
    handleInputChange,
    handleLinkClick,
    handleInputFocus,
    matchedResults,
    filteredCollegeData,
    filteredExamData,
    filteredNewsData,
    filteredCourseData, // Pass only top courses
    isSearchCollegeLoading,
    isSearchExamLoading,
    isSearchCourseData, // courseData,
    topCollegeData,
    // topCourseData,
    courseData,
    isSearchInstituteLoading,
    topSuggestions,
    handleTopSuggestionClick,
    // handleResultClick,
  };
};

export default useUniversalSearchbarHook;
